import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';

import Logo from '@/components/Logo';
import { Button } from '@/components/ui/button';

/**
 * Forgot Password — placeholder page.
 *
 * We don't yet have an email service wired up (user chose to defer that
 * setup), so this page just tells the visitor to contact support. When
 * we add Resend / SendGrid, this page turns into a real form.
 * Route: /forgot-password
 */
const SUPPORT_EMAIL = 'support@theunityblazeconnection.com';

const ForgotPassword = () => {
  const mailtoUrl = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Password reset request')}&body=${encodeURIComponent('Please reset the password for the account associated with this email.')}`;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md space-y-8">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>

        <div className="text-center">
          <Logo size="large" />
          <h1 className="text-3xl font-bold mt-6 mb-2">Forgot your password?</h1>
          <p className="text-muted-foreground">
            Automated password-reset emails are coming soon. In the meantime, our team can help
            you reset it manually.
          </p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <div className="space-y-1">
              <p className="font-medium">Email us for a reset</p>
              <p className="text-sm text-muted-foreground">
                Send a note to <span className="font-mono">{SUPPORT_EMAIL}</span> from the email
                you signed up with. We&apos;ll respond within 1 business day.
              </p>
            </div>
          </div>

          <Button asChild className="w-full" data-testid="forgot-email-btn">
            <a href={mailtoUrl}>
              <Mail className="h-4 w-4 mr-2" /> Email support
            </a>
          </Button>
        </div>

        <p className="text-center text-xs text-muted-foreground">
          Remembered it? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
